import React, { useState, useEffect } from "react";

import { cn } from "@/shared/utils";

export interface SecurityLogoProperties {
  className?: string;
  isin: string;
  name: string;
  size?: number;
}

/**
 * Extract initials from security name
 *
 * @param securityName - The security name to extract initials from
 *
 * @returns {string} The initials (2 characters) or "?" if name is empty
 *
 */
const getInitials = (securityName: string): string => {
  if (!securityName) {
    return "?";
  }

  // Split by common separators and take first 2 words
  const words = securityName
    .split(/[\s\-_&]+/)
    .filter((word) => word.length > 0)
    .slice(0, 2);

  if (words.length === 0) {
    return "?";
  }
  if (words.length === 1) {
    return words[0]?.slice(0, 2).toUpperCase() ?? "??";
  }

  // Take first letter of first two words
  const first = words[0]?.charAt(0) ?? "";
  const second = words[1]?.charAt(0) ?? "";
  return (first + second).toUpperCase();
};

/**
 * SecurityLogo - Displays a security logo from Elbstream API
 * Falls back to initials if logo is not available
 *
 * @param root0
 *
 * @param root0.isin
 *
 * @param root0.name
 *
 * @param root0.size
 *
 * @param root0.className
 *
 * @returns {JSX.Element} The SecurityLogo component
 *
 */
export const SecurityLogo: React.FC<SecurityLogoProperties> = ({
  className,
  isin,
  name,
  size = 40,
}) => {
  const [logoError, setLogoError] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);

  // Generate logo URL
  const logoUrl = `https://api.elbstream.com/logos/isin/${isin}?format=svg`;

  const initials = getInitials(name);

  const handleImageError = () => {
    setLogoError(true);
  };

  const handleImageLoad = () => {
    setLogoLoaded(true);
  };

  // Reset error state when ISIN changes
  useEffect(() => {
    setLogoError(false);
    setLogoLoaded(false);
  }, [isin]);

  // If error occurred, show initials
  if (logoError) {
    return (
      <div
        className={cn(
          "flex items-center justify-center shrink-0 rounded-md bg-gray-700 text-gray-300 font-semibold",
          className,
        )}
        style={{ height: size, width: size }}
      >
        <span className="text-xs">{initials}</span>
      </div>
    );
  }

  // Show logo
  return (
    <div
      className={cn(
        "relative flex items-center justify-center shrink-0 rounded-md bg-gray-800 overflow-hidden",
        className,
      )}
      style={{ height: size, width: size }}
    >
      <img
        alt={`${name} logo`}
        className={cn(
          "max-w-full max-h-full object-contain transition-opacity duration-200",
          !logoLoaded && "opacity-0",
        )}
        loading="lazy"
        onError={handleImageError}
        onLoad={handleImageLoad}
        src={logoUrl}
        style={{ height: size, width: size }}
      />
      {}
      {!logoLoaded && !logoError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-gray-600 border-t-gray-400 rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};
